import {
	Office365Domain,
	UserModelWithLicense,
	createNewUser,
	editUser,
	getAvailableDomains,
	EditableUserFields,
	getEditViewModel,
	getCreateViewModel,
	EditUserModel,
	UpdateUserModel
} from "client/Api/UserManagement/userManagement";
import {
	FunctionComponent,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	EMPTY_ARRAY,
	isNil,
	resolveFormattedTextString,
	hasFlag
} from "client/Misc/utils";
import { tryParseInt } from "client/Misc/parseUtils";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import {
	Form,
	SelectItem,
	SelectOption,
	Tabs,
	ToolbarAction
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { UserAndUserGroupKind } from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import { UserGroupConfiguration, attachToUserGroupConfiguration } from "./UserGroupConfiguration";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import {
	IRootValidation,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { userManagementRouting } from "./UserManagementPage";
import {
	doesNotExceedLength,
	isEmailAddress,
	isNotEmpty,
	isNotNullNorUndefined,
	isValidDate
} from "client/Validation/rules";
import { UserGroupOverviewModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	RouteParametersFromProps,
	connectToRouter,
	createBrowserNavigationUrl
} from "client/Navigation";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { TimeZone } from "client/DateAndTime/timezones";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { configureValidationStep, IObjectValidationStepBuilder } from "client/Validation/steps";
import styles from "./EditUserPage.module.less";
import classNames from "classnames";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import { useChangeUserLicenseConfirmationDialog } from "../Licensing/UserLicenseManagement/ChangeUserLicenseConfirmationDialog";
import { SettingsTab } from "./SettingsTab";
import { UserLicensesTab } from "./UserLicenseTab";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { pushMessage } from "client/Features/Alerts/pageReloadAlerts";
import { PathRedirect } from "client/Validation/serverErrors";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";

export type EditUserPageRouteProps = {
	userId?: number;
	onNavigate: NavigationCallback;
};

export const EditUserPage = (props: EditUserPageRouteProps): ReactElement | null => {
	const {
		userId,
		onNavigate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [domainOptions, setDomainOptions] = useState<readonly SelectItem<string>[] | null>(null);
	const [activeTab, setActiveTab] = useState<TabId>("settings");

	const [formData, setFormData] = useState<FormData>(defaultFormData);
	const [viewModel, setViewModel] = useState<ViewModel>(defaultViewModel);

	const isForNewUser: boolean = isNil(userId);

	const [isEditingUsername, setIsEditingUsername] = useState<boolean>(isForNewUser);

	const [userLicenseConfirmationDialog, openUserLicenseConfirmationDialog] = useChangeUserLicenseConfirmationDialog({
		title: localize("_AreYouSureYouWantToChangeWhichPackageIsAssignedToThisUser"),
		trackingId: "edit-user-module-confirmation-dialog"
	});

	const {
		isCurrentUser,
		editUserFieldsMode,
		userGroups,
		existingDisplayName,
		existingUserLicense
	} = viewModel;

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	useActiveSettingsMenuItem(SettingsPageKind.UserManagement);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const userModelRequest: Promise<EditUserModel> = isNil(userId)
						? getCreateViewModel(signal)
						: getEditViewModel(userId, signal);

					const userModel: EditUserModel = await userModelRequest
							.catch(handlePageDataLoadingError);

					const {
						editUserFieldsMode,
						isCurrentUser,
						selectedUser,
						userGroups
					} = userModel;

					setIsBusy(false);
					const formData: FormData = convertToFormData(selectedUser);
					setFormData(formData);
					setViewModel({
						isCurrentUser,
						editUserFieldsMode,
						userGroups,
						existingUserLicense: formData.userLicense,
						existingDisplayName: isNil(userId)
							? null
							: getComputedDisplayName({
								firstName: selectedUser.firstName,
								lastName: selectedUser.lastName
							})
								.computedDisplayName
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, userId]
	);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const userTimezone: TimeZone = useUserTimezone();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		hasFlag(editUserFieldsMode, EditableUserFields.TheRest)
			? validation
			: "NoValidation"
	);

	useEffect(
		(): (() => void) | void => {
			if (!isEditingUsername) {
				return;
			}

			const loadDomains = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const domains: readonly Office365Domain[] = await getAvailableDomains(signal);

					const options: readonly SelectOption<string>[] = domains.map(
						(x): SelectOption<string> => ({
							text: x.domainName,
							value: x.domainName
						})
					);

					setDomainOptions(options);

					if (options.length > 0) {
						setFormData(
							(formData): FormData => {
								const existingValueExist: boolean = options.some(x => x.value === formData.userPrincipalNameDomain);
								if (existingValueExist) {
									return formData;
								}

								return {
									...formData,
									userPrincipalNameDomain: options[0].value
								};
							}
						);
					}

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError("_GeneralErrorMessage");
					setIsEditingUsername(false);
					setIsBusy(false);
				}
			};

			const cancellation = new AbortController();
			loadDomains(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			isEditingUsername
		]
	);

	const navigateToOverviewHandler = useCallback(
		(shouldReloadPage: boolean): void => {
			const navigation: string = userManagementRouting.overview;

			if (shouldReloadPage) {
				const url: string = createBrowserNavigationUrl(navigation);
				navigateTo(url);
			} else {
				onNavigate(navigation);
			}
		},
		[onNavigate]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const validation: IRootValidation<FormData> = formHandler.validation;

			try {
				const validationResult: InputValidationResult = await validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}


				const model: UserModelWithLicense = convertToSaveModel(formData);

				const wasUserLicenseChanged: boolean = existingUserLicense !== model.userLicense;

				if (wasUserLicenseChanged) {
					if (!await openUserLicenseConfirmationDialog()) {
						setIsBusy(false);
						return;
					}
				}

				const canMutateLicenses: boolean = hasFlag(editUserFieldsMode, EditableUserFields.UserLicense);

				if (isForNewUser) {
					await createNewUser(
						{
							...model,
							userLicense: canMutateLicenses
								? { isSet: true, value: model.userLicense }
								: { isSet: false }
						},
						globalCancellation
					);

					alertManager.showSuccess(localize("_UsersCreated"));
					navigateToOverviewHandler(false);
				} else {
					const canUpdateRestFields: boolean = hasFlag(editUserFieldsMode, EditableUserFields.TheRest);
					const canUpdateAdminFields: boolean = hasFlag(editUserFieldsMode, EditableUserFields.IsAdmin);
					const canUpdateHideFromAddressBookField: boolean = hasFlag(editUserFieldsMode, EditableUserFields.IsHiddenFromAddressBook);

					const {
						id,
						isAdmin,
						isHiddenFromAddressBook,
						userLicense,
						...rest
					} = model;

					const updateUserModel: UpdateUserModel = {
						isAdmin: canUpdateAdminFields
							? { isSet: true, value: isAdmin }
							: { isSet: false },
						isHiddenFromAddressBook: canUpdateHideFromAddressBookField
							? { isSet: true, value: isHiddenFromAddressBook }
							: { isSet: false },
						theRest: canUpdateRestFields
							? { isSet: true, value: rest }
							: { isSet: false },
						userLicense: canMutateLicenses
							? { isSet: true, value: userLicense }
							: { isSet: false },
					};

					await editUser(
						id,
						updateUserModel,
						globalCancellation
					);

					if (isCurrentUser) {
						pushMessage({
							text: localize("_UsersSaved"),
							type: "success"
						});
						navigateToOverviewHandler(true);
					} else {
						alertManager.showSuccess(localize("_UsersSaved"));
						navigateToOverviewHandler(false);
					}

				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);

				if (!validation.tryProcessServerError(e, pathRedirects)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			isBusy,
			formHandler.validation,
			globalCancellation,
			isCurrentUser,
			formData,
			existingUserLicense,
			isForNewUser,
			alertManager,
			openUserLicenseConfirmationDialog,
			navigateToOverviewHandler,
			editUserFieldsMode
		]
	);

	const defaultActions = useMemo(
		(): readonly EditUserAction[] => {
			return listDefaultActions({
				save: saveHandler,
				cancel: () => navigateToOverviewHandler(false),
				editUsername: () => setIsEditingUsername(true)
			});
		},
		[navigateToOverviewHandler, saveHandler]
	);

	const topBarActions = useMemo(
		(): readonly EditUserAction[] => {
			return buildActionsForTopBar<EditUserActionContext, EditUserAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	const actionContext = useMemo(
		(): EditUserActionContext => ({
			isEditingUsername,
			editUserFieldsMode,
			activeTab
		}),
		[
			activeTab,
			isEditingUsername,
			editUserFieldsMode
		]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				onBack={() => navigateToOverviewHandler(false)}
				title={resolveFormattedTextString(
					isForNewUser
						? localize("_NewUser")
						: {
							template: localize("_EditUserX"),
							replacements: [
								{ what: "{{Name}}", filler: existingDisplayName ?? "" }
							]
						}
				)}
				actions={topBarActions}
				actionContext={actionContext}
			>
				<SettingsPage.Section height="stretch">
					<Form onSubmit="NONE" height="fill">
						<Tabs<TabId>
							height="fill"
							activeTabId={activeTab}
							onActiveTabIdChange={setActiveTab}
						>
							<Tabs.Tab<TabId>
								{...formHandler.forStep(settingsValidationStep).attachToTab()}
								id="settings"
								title={localize("_Settings")}
							>
								<div className={classNames(styles.tabContent, styles.tabContent_constrainWidth)}>
									<SettingsTab
										formHandler={formHandler}
										editUserFieldsMode={editUserFieldsMode}
										isEditingUsername={isEditingUsername}
										isCurrentUser={isCurrentUser}
										domainOptions={domainOptions}
										timezone={userTimezone}
									/>
								</div>
							</Tabs.Tab>

							<Tabs.Tab<TabId>
								{...formHandler.forStep(modulesValidationStep).attachToTab()}
								id="modules"
								title={localize("_Package")}
							>
								<div className={styles.tabContent}>
									<UserLicensesTab
										editUserFieldsMode={editUserFieldsMode}
										attachable={formHandler.for("userLicense")}
									/>
								</div>
							</Tabs.Tab>

							<Tabs.Tab<TabId>
								{...formHandler.forStep(groupsValidationStep).attachToTab()}
								id="groups"
								title={localize("_Groups")}
							>
								<div className={styles.tabContent}>
									<UserGroupConfiguration
										{...attachToUserGroupConfiguration(
											formHandler.for("selectedUserGroups"),
											hasFlag(editUserFieldsMode, EditableUserFields.TheRest)
												? UserAndUserGroupKind.Edit
												: UserAndUserGroupKind.Read
										)}
										data={userGroups}
									/>
								</div>
							</Tabs.Tab>
						</Tabs>
					</Form>
				</SettingsPage.Section>
				<AiAssistantFabSpacing />
			</SettingsPage>

			{userLicenseConfirmationDialog}
		</>
	);
};
EditUserPage.displayName = "EditUserPage";

type TabId =
	| "settings"
	| "modules"
	| "groups";

export type EditUserPageRouteParams = RouteParametersFromProps<
	EditUserPageRouteProps,
	"userId"
>;

export const EditUserPageForRouter: FunctionComponent = connectToRouter(
	EditUserPage,
	{
		"userId": {
			isRequired: false,
			tryParse: tryParseInt
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

// NOTE: we leave the userPrincipalName to be able to use
// it for server side validation messages
export type FormData = Omit<UserModelWithLicense, "birthday"> & {
	birthday: Date | null;
	userPrincipalNameName: string;
	userPrincipalNameDomain: string;
};

const defaultFormData: FormData = {
	birthday: null,
	department: null,
	firstName: "",
	id: 0,
	isActive: false,
	isAdmin: false,
	isHiddenFromAddressBook: false,
	jobTitle: "",
	lastName: "",
	mobilePhone: "",
	officeLocation: null,
	primaryEmail: null,
	secondaryEmail: "",
	selectedUserGroups: null,
	userPrincipalName: "",
	userPrincipalNameDomain: "",
	userPrincipalNameName: "",
	userLicense: UserLicense.Simplify
};

type ViewModel = {
	editUserFieldsMode: EditableUserFields;
	isCurrentUser: boolean;
	userGroups: readonly UserGroupOverviewModel[];
	existingUserLicense: UserLicense;
	existingDisplayName: string | null;
};

const defaultViewModel: ViewModel = {
	isCurrentUser: false,
	editUserFieldsMode: EditableUserFields.IsAdmin
		| EditableUserFields.UserLicense
		| EditableUserFields.IsHiddenFromAddressBook
		| EditableUserFields.TheRest,
	userGroups: EMPTY_ARRAY,
	existingUserLicense: UserLicense.Simplify,
	existingDisplayName: null
};

function convertToFormData(model: UserModelWithLicense): FormData {
	const splitUpn: string[] = model.userPrincipalName
		.split("@");

	return {
		...model,
		birthday: parseDtoDate(model.birthday),
		primaryEmail: model.primaryEmail ?? "",
		userPrincipalNameName: splitUpn[0],
		userPrincipalNameDomain: splitUpn[1] ?? ""
	};
}

function convertToSaveModel(formData: FormData): UserModelWithLicense {
	const {
		id,
		firstName,
		lastName,
		userPrincipalNameName,
		userPrincipalNameDomain,
		secondaryEmail,
		jobTitle,
		department,
		officeLocation,
		mobilePhone,
		isActive,
		isAdmin,
		isHiddenFromAddressBook,
		selectedUserGroups,
		userLicense
	} = formData;

	let { birthday, primaryEmail } = formData;

	if (birthday) {
		birthday = new Date(
			Date.UTC(
				birthday.getFullYear(),
				birthday.getMonth(),
				birthday.getDate()
			)
		);
	}

	if (primaryEmail?.trim() === "") {
		primaryEmail = null;
	}

	return {
		id,
		firstName,
		lastName,
		userPrincipalName: userPrincipalNameName + "@" + userPrincipalNameDomain,
		primaryEmail,
		secondaryEmail,
		jobTitle,
		department,
		officeLocation,
		mobilePhone,
		birthday,
		isActive,
		isAdmin,
		isHiddenFromAddressBook,
		selectedUserGroups,
		userLicense
	};
}

type EditUserActionContext = {
	isEditingUsername: boolean;
	editUserFieldsMode: EditableUserFields;
	activeTab: TabId;
};

type EditUserAction = ToolbarAction<EditUserActionContext>;

type DefaultActionsHandlers = {
	save: () => void;
	cancel: () => void;
	editUsername: () => void;
};

function listDefaultActions(handlers: DefaultActionsHandlers): readonly EditUserAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			icon: "floppy-disk",
			canExecute: () => true,
			execute: () => handlers.save(),
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: () => true,
			execute: () => handlers.cancel()
		},
		{
			kind: ActionKind.Regular,
			name: "editUsername",
			title: localize("_EditUsername"),
			icon: "pen",
			canExecute: (c) =>
				hasFlag(c.editUserFieldsMode, EditableUserFields.TheRest)
				&& !c.isEditingUsername
				&& c.activeTab === "settings",
			execute: () => handlers.editUsername()
		}
	];
}

const validation = createValidationConfiguration<FormData>();

validation.forScalar("firstName")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("lastName")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("userPrincipalNameName")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("userPrincipalNameDomain")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("primaryEmail")
	.add(
		isEmailAddress()
	);

validation.forScalar("secondaryEmail")
	.add(
		isEmailAddress()
	);

validation.forScalar("department")
	.add(
		doesNotExceedLength(64)
	);

validation.forScalar("officeLocation")
	.add(
		doesNotExceedLength(128)
	);

validation.forScalar("birthday")
	.add(
		isValidDate()
	);

validation.forScalar("mobilePhone")
	.add(
		doesNotExceedLength(255)
	);

const settingsValidationStep: IObjectValidationStepBuilder<FormData> = configureValidationStep<FormData>()
	.add(
		"firstName",
		"lastName",
		"userPrincipalNameName",
		"userPrincipalNameDomain",
		"primaryEmail",
		"secondaryEmail",
		"department",
		"officeLocation",
		"mobilePhone",
		"birthday"
	);

const groupsValidationStep: IObjectValidationStepBuilder<FormData> = configureValidationStep<FormData>()
	.add(
		"selectedUserGroups"
	);

const modulesValidationStep: IObjectValidationStepBuilder<FormData> = configureValidationStep<FormData>()
	.add(
		"userLicense"
	);

const pathRedirects: readonly PathRedirect[] = [
	{
		pattern: "UserLicense.Value",
		replacement: "UserLicense"
	},
	{
		pattern: "IsAdmin.Value",
		replacement: "IsAdmin"
	},
	{
		pattern: "IsHiddenFromAddressBook.Value",
		replacement: "IsHiddenFromAddressBook"
	},
	{
		pattern: new DotPathWildcardPattern("TheRest.Value.**"),
		replacement: "{0}"
	}
];
